/**
 * Date-free indicative display only. These amounts do not account for duration,
 * fees or availability, and must never replace server quotes.
 */
export type DiscoveryRentalType = "driving" | "chauffeured" | "event";
export type DiscoveryPriceUnit = "day" | "hour" | "flat";
export interface DiscoveryPriceSource {
  priceDriving?: number | null;
  priceEvent?: number | null;
  chauffeurMinPrice?: number | null;
  eventMinPrice?: number | null;
  minimumPrice?: number | null;
  chauffeuredHourlyRate?: number | null;
  eventHourlyRate?: number | null;
  rentalDrivingActive?: boolean;
  rentalChauffeurActive?: boolean;
  rentalEventActive?: boolean;
}
export interface DiscoveryPrice {
  amount: number;
  unit: DiscoveryPriceUnit;
  /** Generic unfiltered minima have no unambiguous owning service. */
  rentalType: DiscoveryRentalType | null;
}
const positive = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v) && v > 0;

export function resolveDiscoveryPrice(
  car: DiscoveryPriceSource,
  selected?: DiscoveryRentalType | null,
): { primary: DiscoveryPrice | null; hourly: DiscoveryPrice | null } {
  const days: DiscoveryPrice[] = [];
  const hours: DiscoveryPrice[] = [];
  const add = (
    list: DiscoveryPrice[], amount: unknown, unit: DiscoveryPriceUnit,
    rentalType: DiscoveryRentalType | null,
  ) => { if (positive(amount)) list.push({ amount, unit, rentalType }); };
  const lowest = (list: DiscoveryPrice[]) =>
    list.reduce<DiscoveryPrice | null>((best, p) => !best || p.amount < best.amount ? p : best, null);

  if (selected) {
    const active = selected === "driving" ? car.rentalDrivingActive
      : selected === "event" ? car.rentalEventActive : car.rentalChauffeurActive;
    if (!active) return { primary: null, hourly: null };
    if (selected === "driving") {
      add(days, car.priceDriving, "day", selected);
    } else {
      // Booking rules use the generic floor for chauffeur/event only, when
      // the selected service has no positive specific floor. Never for driving.
      const floor = selected === "event" ? car.eventMinPrice : car.chauffeurMinPrice;
      add(days, positive(floor) ? floor : car.minimumPrice, "flat", selected);
      if (selected === "event" && !days.length) add(days, car.priceEvent, "day", selected);
      add(hours, selected === "event" ? car.eventHourlyRate : car.chauffeuredHourlyRate, "hour", selected);
    }
  } else {
    // Web no-type card candidate order is intentional: ties retain the
    // original daily/minimum meaning instead of inventing a billing unit.
    if (car.rentalDrivingActive) add(days, car.priceDriving, "day", "driving");
    if (car.rentalEventActive) {
      add(days, car.priceEvent, "day", "event");
      add(days, car.eventMinPrice, "flat", "event");
    }
    if (car.rentalChauffeurActive) add(days, car.chauffeurMinPrice, "flat", "chauffeured");
    if (car.rentalDrivingActive || car.rentalEventActive || car.rentalChauffeurActive)
      add(days, car.minimumPrice, "flat", null);
    if (car.rentalEventActive) add(hours, car.eventHourlyRate, "hour", "event");
    if (car.rentalChauffeurActive) add(hours, car.chauffeuredHourlyRate, "hour", "chauffeured");
  }
  const primary = lowest(days);
  const hourly = lowest(hours);
  return primary ? { primary, hourly } : { primary: hourly, hourly: null };
}
